/**
 * FILE: CalibrationModal.js
 * PURPOSE: VANE Progressive Gating — JIT trait collection before a vote.
 *
 * FLOW:
 *   1. User clicks "Vote" on a gated poll
 *   2. PollCard calls api.gateCheck(pollId, userId)
 *   3. If TRAIT_REQUIRED → show this modal asking for the missing trait
 *   4. User answers → api.updateTraits() saves it → re-run gate check
 *   5. If their answer PASSES → close modal, proceed to vote  
 *   6. If their answer FAILS → show graceful rejection screen
 *   7. If DISQUALIFIED (already known to fail) → show rejection immediately
 *   8. If PASS → skip this modal entirely
 *
 * DESIGN:
 *   - Feels like a natural onboarding step, not a blocker
 *   - Rejection is graceful and warm — never hostile
 *   - Age & Gender are NEVER asked here (mandatory onboarding traits)
 */
import React, { useState } from 'react';
import {
  Shield, X, CheckCircle2, Lock, Users,
  GraduationCap, Briefcase, BarChart, MapPin,
  Sparkles, ArrowRight, Heart
} from 'lucide-react';
import { useModalA11y } from '../hooks/useModalA11y';

// ─── Trait config (matches gating.ts TraitKey) ────────────────────────────────
const TRAIT_CONFIG = {
  education: {
    icon: <GraduationCap size={28} />,
    label: 'Education Level',
    prompt: 'What is your highest level of education?',
    color: 'from-blue-500 to-indigo-600',
    options: [
      { value: 'Secondary', label: 'Secondary School' },
      { value: 'Undergraduate', label: 'Undergraduate Degree' },
      { value: 'Postgraduate', label: 'Postgraduate / Masters' },
      { value: 'Doctorate', label: 'Doctorate / PhD' },
      { value: 'Vocational', label: 'Vocational / Technical' },
    ]
  },
  workStatus: {
    icon: <Briefcase size={28} />,
    label: 'Work Status',
    prompt: 'What is your current work status?',
    color: 'from-emerald-500 to-teal-600',
    options: [
      { value: 'Student', label: 'Student' },
      { value: 'Employed', label: 'Full-time Employed' },
      { value: 'Self-Employed', label: 'Self-Employed / Freelance' },
      { value: 'Unemployed', label: 'Currently Seeking Work' },
      { value: 'Retired', label: 'Retired' },
    ]
  },
  sector: {
    icon: <BarChart size={28} />,
    label: 'Professional Sector',
    prompt: 'Which sector best describes your work or study?',
    color: 'from-orange-500 to-amber-600',
    options: [
      { value: 'Tech', label: 'Technology & Software' },
      { value: 'Finance', label: 'Finance & Banking' },
      { value: 'Healthcare', label: 'Healthcare & Medicine' },
      { value: 'Education', label: 'Education & Research' },
      { value: 'Government', label: 'Government & Public Sector' },
      { value: 'Creative', label: 'Creative & Media' },
      { value: 'Other', label: 'Other' },
    ]
  },
  region: {
    icon: <MapPin size={28} />,
    label: 'Your Region',
    prompt: 'Which region are you currently based in?',
    color: 'from-purple-500 to-violet-600',
    options: [
      { value: 'North America', label: 'North America' },
      { value: 'Europe', label: 'Europe' },
      { value: 'Asia', label: 'Asia & Pacific' },
      { value: 'South America', label: 'South America' },
      { value: 'Africa', label: 'Africa' },
      { value: 'Middle East', label: 'Middle East' },
      { value: 'Oceania', label: 'Oceania' },
    ]
  },
};

// ─── Graceful Rejection Screen ────────────────────────────────────────────────
function RejectionScreen({ onClose }) {
  return (
    <div className="flex flex-col items-center justify-center p-10 text-center flex-1">
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-rose-500/20 blur-[40px] rounded-full" />
        <div className="relative w-24 h-24 bg-gradient-to-br from-rose-400 to-pink-600 rounded-[28px] flex items-center justify-center shadow-2xl shadow-rose-500/30">
          <Heart size={40} className="text-white" />
        </div>
      </div>

      <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-3 uppercase italic tracking-tighter">
        This one's not for you
      </h3>
      <p className="text-sm font-bold text-gray-500 dark:text-gray-400 leading-relaxed max-w-[260px] mb-8">
        This poll is designed for a specific audience — and that's okay. Your unique perspective shapes thousands of other polls on the network.
      </p>

      <div className="w-full p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-100 dark:border-purple-800/30 mb-6">
        <p className="text-[11px] font-black text-primary-600 uppercase tracking-widest flex items-center justify-center gap-2">
          <Sparkles size={12} />
          Your voice still counts
        </p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 font-bold mt-1">
          Explore the Global Feed — hundreds of polls await your verified opinion.
        </p>
      </div>

      <button
        onClick={onClose}
        className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-black uppercase tracking-wider text-sm hover:opacity-90 transition-all flex items-center justify-center gap-2"
      >
        Explore Other Polls <ArrowRight size={16} />
      </button>
    </div>
  );
}

// ─── Main CalibrationModal ────────────────────────────────────────────────────
export function CalibrationModal({
  isOpen,
  missingCriteria = [],       // CreatorCriterion[] from gate-check TRAIT_REQUIRED
  isDisqualified = false,      // true when gate-check returns DISQUALIFIED
  currentCriterionIndex = 0,
  onAnswer,                    // (traitKey, value) → async save + re-check
  onClose,
}) {
  const [view, setView] = useState(isDisqualified ? 'rejected' : 'question'); // 'question' | rejected' | 'success'
  const [isAnswering, setIsAnswering] = useState(false);
  const [stepIndex, setStepIndex] = useState(currentCriterionIndex);
  const containerRef = useModalA11y(isOpen, onClose);

  if (!isOpen) return null;

  const currentCriterion = missingCriteria[stepIndex];
  const traitDef = currentCriterion ? TRAIT_CONFIG[currentCriterion.trait_key] : null;
  const totalSteps = missingCriteria.length;

  const handleOptionClick = async (value) => {
    if (isAnswering || !currentCriterion) return;
    setIsAnswering(true);

    try {
      const passed = await onAnswer(currentCriterion.trait_key, value);

      if (!passed) {
        setView('rejected');
        return;
      }

      // Passed this criterion — next one or done
      if (stepIndex < totalSteps - 1) {
        setStepIndex(prev => prev + 1);
      } else {
        setView('success');
        setTimeout(onClose, 1200);
      }
    } finally {
      setIsAnswering(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" onClick={onClose} />

      <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Voter calibration" className="relative bg-white dark:bg-gray-900 rounded-panel shadow-2xl border border-gray-100 dark:border-gray-800 w-full max-w-sm overflow-hidden flex flex-col"
        style={{ minHeight: '420px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-primary-600" />
            <span className="text-2xs font-black uppercase tracking-widest text-gray-500">Voter Calibration</span>
          </div>
          {view !== 'success' && (
            <button onClick={onClose} aria-label="Close calibration dialog" className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all text-gray-500">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Content */}
        {view === 'rejected' && <RejectionScreen onClose={onClose} />}

        {view === 'success' && (
          <div className="flex flex-col items-center justify-center p-10 text-center flex-1">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 size={36} className="text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Verified!</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-bold">Your vote has been counted at <span className="text-green-600 dark:text-green-400">1.0× weight</span>.</p>
          </div>
        )}

        {view === 'question' && traitDef && (
          <div className="flex flex-col flex-1">
            {/* Progress */}
            {totalSteps > 1 && (
              <div className="px-6 pt-4 flex gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= stepIndex ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
                ))}
              </div>
            )}

            {/* Trait icon + prompt */}
            <div className="px-6 pt-6 pb-4">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${traitDef.color} flex items-center justify-center mb-4 shadow-lg text-white`}>
                {traitDef.icon}
              </div>
              <p className="text-2xs font-black text-gray-500 uppercase tracking-[0.2em] mb-1">{traitDef.label}</p>
              <h3 className="text-xl font-black text-gray-900 dark:text-white leading-tight">
                {traitDef.prompt}
              </h3>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-500 mt-1.5">
                This is stored securely in your Ghost Vault and used only to qualify your vote.
              </p>
            </div>

            {/* Options */}
            <div className="px-6 pb-6 flex-1 overflow-y-auto space-y-2">
              {(currentCriterion.options?.length > 0 ? currentCriterion.options : traitDef.options).map((opt) => {
                const value = typeof opt === 'string' ? opt : opt.value;
                const label = typeof opt === 'string' ? opt : opt.label;
                return (
                  <button
                    key={value}
                    onClick={() => handleOptionClick(value)}
                    disabled={isAnswering}
                    className="w-full p-3.5 bg-gray-50 dark:bg-gray-800 hover:bg-primary-600/10 dark:hover:bg-primary-600/20 border border-gray-200 dark:border-gray-700 hover:border-primary-600/50 dark:hover:border-primary-600/50 rounded-xl font-bold text-sm text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-600 transition-all text-left flex items-center justify-between group disabled:opacity-50"
                  >
                    <span>{label}</span>
                    <ArrowRight size={14} className="text-gray-300 dark:text-gray-600 group-hover:text-primary-600 transition-colors" />
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex items-center justify-center gap-2 opacity-50">
              <Lock size={11} className="text-gray-500 dark:text-gray-500" />
              <span className="text-2xs font-black uppercase text-gray-500 dark:text-gray-500 tracking-widest">Anonymous · Encrypted</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalibrationModal;
